<!-- eslint-disable no-irregular-whitespace -->
<template>
  <b-row class="mx-0">
    <b-col
      class="px-0 pb-4"
      cols="12"
    >
      <h2 class="mt-4">
        Palette de couleurs
      </h2>
    </b-col>
    <b-col
      lg="6"
      class="p-0"
    >
      <input
        id="copy"
        ref="copy"
        class="sr-only"
        aria-hidden="true"
        type="text"
        value=""
      />
      <div
        v-show="copied[0] !== 'g'"
        class="alert alert-secondary position-fixed fixed-top m-4 ml-auto"
        aria-hidden="true"
        style="z-index: 2000; width: 200px"
      >
        <i :class="`fas fa-circle fc-${copied[0]}${copied[1]}`"></i>
        <b v-text="colors[copied[0]][copied[1] - 1]"></b> copié !
      </div>
      <table class="table table-borderless border-0">
        <caption class="sr-only">
          Palette de couleurs
        </caption>
        <thead>
          <tr>
            <th
              class="fb-g10 fc-light"
              scope="col"
            >
              <span class="name">Gris / Indigo</span><br />
              <span>(g10)</span><br />
              <span class="hex">#0b1c54</span>
            </th>
            <th
              class="fb-f7 fc-light"
              scope="col"
            >
              <span class="name">Violet</span><br />
              <span>(f7)</span><br />
              <span class="hex">#725794</span>
            </th>
            <th
              class="fb-b6 fc-light"
              scope="col"
            >
              <span class="name">Bleu</span><br />
              <span>(b6)</span><br />
              <span class="hex">#487982</span>
            </th>
          </tr>
        </thead>
        <tbody class="text-monospace">
          <tr
            v-for="n in 10"
            :key="n"
          >
            <td
              v-for="c in Object.keys(colors).slice(0, 3)"
              :key="c"
            >
              <a
                :id="`color-${c}${n}`"
                href="#"
                class="d-block text-center"
                @click.prevent.stop="copyColor(c, n)"
              >
                <span class="fa-stack fa-lg">
                  <i :class="`fas fa-circle fa-stack-2x fc-${c}${n}`"></i>
                  <i
                    :class="`fas fa-check fa-stack-1x ${
                      rlight[c][0] === n ? 'fc-light' : ''} ${
                      rdark[c][0] === n ? 'fc-dark' : ''} ${
                      rdark[c][0] !== n && rlight[c][0] !== n ? 'fc-0' : ''}`"
                  ></i>
                </span>
                <span
                  class="sr-only"
                  v-html="colors[c][n - 1]"
                ></span>
                <span
                  v-if="rlight[c][0] === n"
                  class="sr-only"
                  v-text="'Ratio > 4.5:1 (fond blanc)'"
                ></span>
                <span
                  v-if="rdark[c][0] === n"
                  class="sr-only"
                  v-text="'Ratio > 4.5:1 (texte noir)'"
                ></span>
              </a>

              <b-popover
                :target="`color-${c}${n}`"
                triggers="hover focus"
                placement="topright"
              >
                <h5
                  class="text-center"
                  v-text="`${c}${n}`"
                ></h5>
                <p>
                  <b>Hex :</b>
                  <span v-text="colors[c][n - 1]"></span>
                  <br />
                  <b>RGB :</b>
                  <span v-text="hex2rgb(colors[c][n - 1]).join(', ')"></span>
                  <br />
                  <b>HSV :</b>
                  <span v-text="hex2hsv(colors[c][n - 1]).join(', ')"></span>
                </p>
                <p>
                  <b>Accessibilité :</b>
                  <br />
                  <span class="fa-stack fa-lg">
                    <i class="fas fa-circle fa-stack-2x fc-0"></i>
                    <i
                      :class="`fas fa-${rlight[c][0] <= n ? 'eye' : 'eye-slash'
                      } fc-${c}${n} fa-stack-1x`"
                    ></i>
                  </span>
                  <span v-html="rlight[c][n]"></span>
                  <span v-if="rlight[c][0] <= n">
                    <i class="fas fa-check"></i>
                    AAA
                  </span>
                  <span v-if="headers[c] === n">
                    <i class="fas fa-exclamation-triangle"></i>
                    AA
                  </span>
                  <br />
                  <span class="fa-stack fa-lg">
                    <i :class="`fas fa-circle fa-stack-2x fc-${c}${n}`"></i>
                    <i
                      :class="`fas fa-${rdark[c][0] >= n ? 'eye' : 'eye-slash'
                      } fa-stack-1x fc-dark`"
                    ></i>
                  </span>
                  <span v-html="rdark[c][n]"></span>
                  <span v-if="rdark[c][0] >= n">
                    <i class="fas fa-check"></i>
                    AAA
                  </span>
                </p>
              </b-popover>
            </td>
          </tr>
        </tbody>
      </table>
    </b-col>
    <b-col
      lg="6"
      class="p-0"
    >
      <table class="table table-borderless border-0">
        <caption class="sr-only">
          Palette de couleurs
        </caption>
        <thead>
          <tr>
            <th
              class="fb-v6 fc-light"
              scope="col"
            >
              <span class="name">Vert</span><br />
              <span>(v6)</span><br />
              <span class="hex">#338650</span>
            </th>
            <th
              class="fb-o6 fc-light"
              scope="col"
            >
              <span class="name">Orange</span><br />
              <span>(o5/<u>o6</u>/o7)</span><br />
              <span class="hex">#dd6418</span>
            </th>
            <th
              class="fb-r8 fc-light"
              scope="col"
            >
              <span class="name">Rouge</span><br />
              <span>(r8)</span><br />
              <span class="hex">#c42719</span>
            </th>
          </tr>
        </thead>
        <tbody class="text-monospace">
          <tr
            v-for="n in 10"
            :key="n"
          >
            <td
              v-for="c in Object.keys(colors).slice(3, 6)"
              :key="c"
            >
              <a
                :id="`color-${c}${n}`"
                href="#"
                class="d-block text-center"
                @click.prevent.stop="copyColor(c, n)"
              >
                <span class="fa-stack fa-lg">
                  <i :class="`fas fa-circle fa-stack-2x fc-${c}${n}`"></i>
                  <i
                    :class="`fas fa-check fa-stack-1x ${
                      rlight[c][0] === n ? 'fc-light' : ''} ${
                      rdark[c][0] === n ? 'fc-dark' : ''} ${
                      rdark[c][0] !== n && rlight[c][0] !== n ? 'fc-0' : ''}`"
                  ></i>
                </span>
                <span
                  class="sr-only"
                  v-html="colors[c][n - 1]"
                ></span>
                <span
                  v-if="rlight[c][0] === n"
                  class="sr-only"
                  v-text="'Ratio > 4.5:1 (fond blanc)'"
                ></span>
                <span
                  v-if="rdark[c][0] === n"
                  class="sr-only"
                  v-text="'Ratio > 4.5:1 (texte noir)'"
                ></span>
              </a>

              <b-popover
                :target="`color-${c}${n}`"
                triggers="hover focus"
                placement="topright"
              >
                <h5
                  class="text-center"
                  v-text="`${c}${n}`"
                ></h5>
                <p>
                  <b>Hex :</b>
                  <span v-text="colors[c][n - 1]"></span>
                  <br />
                  <b>RGB :</b>
                  <span v-text="hex2rgb(colors[c][n - 1]).join(', ')"></span>
                  <br />
                  <b>HSV :</b>
                  <span v-text="hex2hsv(colors[c][n - 1]).join(', ')"></span>
                </p>
                <p>
                  <b>Accessibilité :</b>
                  <br />
                  <span class="fa-stack fa-lg">
                    <i class="fas fa-circle fa-stack-2x fc-0"></i>
                    <i
                      :class="`fas fa-${rlight[c][0] <= n ? 'eye' : 'eye-slash'
                      } fc-${c}${n} fa-stack-1x`"
                    ></i>
                  </span>
                  <span v-html="rlight[c][n]"></span>
                  <span v-if="rlight[c][0] <= n">
                    <i class="fas fa-check"></i>
                    AAA
                  </span>
                  <span v-if="headers[c] === n">
                    <i class="fas fa-exclamation-triangle"></i>
                    AA
                  </span>
                  <br />
                  <span class="fa-stack fa-lg">
                    <i :class="`fas fa-circle fa-stack-2x fc-${c}${n}`"></i>
                    <i
                      :class="`fas fa-${rdark[c][0] >= n ? 'eye' : 'eye-slash'
                      } fa-stack-1x fc-dark`"
                    ></i>
                  </span>
                  <span v-html="rdark[c][n]"></span>
                  <span v-if="rdark[c][0] >= n">
                    <i class="fas fa-check"></i>
                    AAA
                  </span>
                </p>
              </b-popover>
            </td>
          </tr>
        </tbody>
      </table>
    </b-col>
  </b-row>
</template>

<style>
table .fa-stack {
  text-shadow: 0 0 1px #a0aec0;
  cursor: pointer;
}

.popover .fa-circle,
.fa-circle.fc-light {
  text-shadow: 0 0 1px #a0aec0;
}
</style>

<script>
export default {
  data() {
    const rdark = {};
    const rlight = {};
    const headers = {};
    const colors = {
      g: ['#f7fafc', '#edf1f7', '#e2e7f0', '#cbd7e0', '#a0abc0', '#677490', '#4a5268', '#2d3448', '#1a1e2c', '#0b1c54'],
      f: ['#fbf5ff', '#edd8fd', '#dcbcfa', '#c194f4', '#a87ede', '#977ac2', '#725794', '#50386e', '#3d2957', '#400180'],
      b: ['#edfeff', '#b3e4e6', '#8ccfd1', '#6badb3', '#55919e', '#487982', '#344c52', '#273236', '#112429', '#003d52'],
      v: ['#f2fff5', '#8feba4', '#6ad481', '#4fb86b', '#3ba161', '#338650', '#29573b', '#213b2d', '#0b2e19', '#005727'],
      o: ['#fff9f0', '#fee7c8', '#fbc98d', '#f6a055', '#fc8e3a', '#dd6418', '#cc4e13', '#9c3c1c', '#7b2f1a', '#c43800'],
      r: ['#fff6f5', '#fedcd7', '#febbb2', '#fc8f81', '#f57665', '#e65740', '#d93e25', '#c42719', '#9c221c', '#c51802'],
    };
    Object.keys(colors).forEach((c) => {
      headers[c] = 1;
      rlight[c] = [1];
      rdark[c] = [1];
      for (let n = 1; n <= 9; n += 1) {
        if (headers[c] === 1 && this.a11y('#ffffff', colors[c][n - 1], 3, 4.51)) {
          headers[c] = n;
        }

        if (rlight[c][0] === 1 && this.a11y('#ffffff', colors[c][n - 1], 4.5, 20)) {
          rlight[c][0] = n;
        }
        rlight[c][n] = this.contrast(this.hex2rgb('#ffffff'), this.hex2rgb(colors[c][n - 1]))
          .toPrecision(3);

        if (this.a11y('#1f1f1f', colors[c][n - 1], 4.5, 20)) {
          rdark[c][0] = n;
        }
        rdark[c][n] = this.contrast(this.hex2rgb('#1f1f1f'), this.hex2rgb(colors[c][n - 1]))
          .toPrecision(3);
      }
    });

    return {
      copied: ['g', '1'],
      colors,
      headers,
      rdark,
      rlight,
    };
  },
  methods: {
    hex2rgb(hex) {
      return hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i,
        (m, r, g, b) => ['#', r, r, g, g, b, b].join(''))
        .substring(1).match(/.{2}/g)
        .map(x => parseInt(x, 16));
    },
    rgb2hsv(rgb) { // https://stackoverflow.com/a/8023734
      const [r, g, b] = [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255];
      let [rr, gg, bb, h, s] = [0, 0, 0, 0, 0];
      const [max, min] = [Math.max(r, g, b), Math.min(r, g, b)];
      const [v, d] = [max, max - min];
      const dc = c => (v - c) / 6 / d + 1 / 2;
      if (d !== 0) {
        s = d / v;
        rr = dc(r);
        gg = dc(g);
        bb = dc(b);

        switch (max) {
          case r:
            h = bb - gg;
            break;
          case g:
            h = (1 / 3) + rr - bb;
            break;
          case b:
            h = (2 / 3) + gg - rr;
            break;
          default:
            // no-default;
            break;
        }
        if (h < 0) {
          h += 1;
        } else if (h > 1) {
          h -= 1;
        }
      }
      return [Math.floor(h * 360), Math.floor(s * 100), Math.floor(v * 100)];
    },
    hex2hsv(hex) {
      return this.rgb2hsv(this.hex2rgb(hex));
    },
    // https://stackoverflow.com/a/9733420
    luminanace(r, g, b) {
      const a = [r, g, b].map((v) => {
        const v2 = v / 255;
        return v2 <= 0.03928
          ? v2 / 12.92
          : ((v2 + 0.055) / 1.055) ** 2.4;
      });
      return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
    },
    contrast(rgb1, rgb2) {
      const lum1 = this.luminanace(rgb1[0], rgb1[1], rgb1[2]);
      const lum2 = this.luminanace(rgb2[0], rgb2[1], rgb2[2]);
      const brightest = Math.max(lum1, lum2);
      const darkest = Math.min(lum1, lum2);
      return ((brightest + 0.05) / (darkest + 0.05));
    },
    a11y(hex1, hex2, r1, r2) {
      return this.contrast(this.hex2rgb(hex1), this.hex2rgb(hex2)) >= r1
        && this.contrast(this.hex2rgb(hex1), this.hex2rgb(hex2)) <= r2;
    },
    copyColor(c, n) {
      const input = this.$refs.copy;
      input.value = this.colors[c][n - 1];
      input.select();
      input.setSelectionRange(0, 7);
      document.execCommand('copy');
      this.copied = [c, n];
      setTimeout(() => { this.copied = ['g', '1']; }, 2000);
    },
  },
};
</script>
